import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import aboutStyle from './About.module.scss';
import commonStyle from '../../styles/commonStyles.module.scss';
import IButton from '../../components/elements/iButton/IButton';
import SectionHeading from '../../components/elements/sectionHeading/SectionHeading';
import Reveal from '../../components/elements/reveal/Reveal';
import Company from '../../components/sections/myJourney/company/Company';
import Contact from '../../components/sections/contact/Contact';
import { myJourney } from '../../data/MyJourney';
import { stackGroups } from '../../data/skills';
import { profile } from '../../data/profile';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

const About = observer(() => {
    const { t } = useTranslation();

    const downloadHandler = () => {
        const link = document.createElement('a');
        link.href = window.location.origin + profile.resumePath;
        link.download = profile.resumeFilename;
        link.click();
    };

    const facts = [
        { label: t('about.factRole'), value: `${profile.role} @ ${profile.company}` },
        { label: t('about.factLocation'), value: profile.location },
        { label: t('about.factEducation'), value: t('about.educationShort') },
        { label: t('about.factLanguages'), value: t('about.languagesValue') },
    ];

    return (
        <>
            <section className={aboutStyle.section}>
                <div className={aboutStyle.inner}>
                    <SectionHeading eyebrow={t('about.eyebrow')} title={t('about.title')} description={t('about.summary')} />

                    <div className={aboutStyle.layout}>
                        <aside className={aboutStyle.rail}>
                            <div className={aboutStyle.factsCard}>
                                <dl className={aboutStyle.facts}>
                                    {facts.map((fact) => (
                                        <div key={fact.label} className={aboutStyle.fact}>
                                            <dt className={aboutStyle.factLabel}>{fact.label}</dt>
                                            <dd className={aboutStyle.factValue}>{fact.value}</dd>
                                        </div>
                                    ))}
                                </dl>
                                <IButton
                                    title={t('about.getMyResume')}
                                    onClick={downloadHandler}
                                    variant='primary'
                                    fullWidth
                                    icon={<ArrowDownwardIcon />}
                                />
                            </div>
                        </aside>

                        <div className={aboutStyle.content}>
                            <Reveal>
                                <div className={aboutStyle.block}>
                                    <h2 className={aboutStyle.blockTitle}>{t('about.experience')}</h2>
                                    <ol className={aboutStyle.timeline}>
                                        {myJourney.map((company, i) => (
                                            <Company key={`${company.companyName}-${i}`} company={company} delay={i * 80} expanded />
                                        ))}
                                    </ol>
                                </div>
                            </Reveal>

                            <Reveal>
                                <div className={aboutStyle.block}>
                                    <h2 className={aboutStyle.blockTitle}>{t('about.skills')}</h2>
                                    <dl className={aboutStyle.stackList}>
                                        {stackGroups.map((group) => (
                                            <div key={group.title} className={aboutStyle.stackRow}>
                                                <dt className={aboutStyle.stackLabel}>{group.title}</dt>
                                                <dd className={commonStyle.tagRow}>
                                                    {group.items.map((item) => (
                                                        <span key={item} className={commonStyle.tag}>
                                                            {item}
                                                        </span>
                                                    ))}
                                                </dd>
                                            </div>
                                        ))}
                                    </dl>
                                </div>
                            </Reveal>

                            <Reveal>
                                <div className={aboutStyle.block}>
                                    <h2 className={aboutStyle.blockTitle}>{t('about.education')}</h2>
                                    <div className={aboutStyle.entry}>
                                        <span className={aboutStyle.entryPeriod}>2017 — 2020</span>
                                        <h3 className={aboutStyle.entryTitle}>{t('about.degree')}</h3>
                                        <p className={aboutStyle.entryText}>{t('about.degreePlace')}</p>
                                    </div>
                                    <div className={aboutStyle.entry}>
                                        <span className={aboutStyle.entryPeriod}>2007 — 2010</span>
                                        <h3 className={aboutStyle.entryTitle}>{t('about.highSchool')}</h3>
                                    </div>
                                </div>
                            </Reveal>

                            <Reveal>
                                <div className={aboutStyle.block}>
                                    <h2 className={aboutStyle.blockTitle}>{t('about.beyondWork')}</h2>
                                    <div className={aboutStyle.entry}>
                                        <span className={aboutStyle.entryPeriod}>2019 — 2020</span>
                                        <h3 className={aboutStyle.entryTitle}>{t('about.volunteer1Title')}</h3>
                                        <p className={aboutStyle.entryText}>{t('about.volunteer1Text')}</p>
                                    </div>
                                    <div className={aboutStyle.entry}>
                                        <span className={aboutStyle.entryPeriod}>2018 — 2020</span>
                                        <h3 className={aboutStyle.entryTitle}>{t('about.volunteer2Title')}</h3>
                                        <p className={aboutStyle.entryText}>{t('about.volunteer2Text')}</p>
                                    </div>
                                    <div className={aboutStyle.entry}>
                                        <span className={aboutStyle.entryPeriod}>2010 — 2013</span>
                                        <h3 className={aboutStyle.entryTitle}>{t('about.militaryTitle')}</h3>
                                        <p className={aboutStyle.entryText}>{t('about.militaryText')}</p>
                                    </div>
                                </div>
                            </Reveal>
                        </div>
                    </div>
                </div>
            </section>

            <Contact />
        </>
    );
});

export default About;
